//@flow
import React from "react";
import getProperty from "lodash.get";
import { Cell } from "fixed-data-table-2";
import s from "./LineItemTableCell.less";
import { percentOfTotal, numberToFixed } from "../../../../../helpers";
import getSymbolFromCurrency from "currency-symbol-map";

export const addCurrencySymbol = (currencyCode, price) => {
  if (price === undefined || price === null) return "";
  return getSymbolFromCurrency(currencyCode) ? getSymbolFromCurrency(currencyCode) + price : price;
};

export const isInputColumn = (columnName: string) => {
  const inputColumns = [
    "FCTY UNIT PRICE",
    "FCTY PRICE UOM",
    "FCTY CURR",
    "OUTSOURCED LABOR COST CURRENCY",
    "OUTSOURCED LABOR COST",
    "UNIT PRICE UPCHG",
    "UPCHG DESC",
    "NET USAGE",
    "WASTE USAGE",
    "CONS CONV RATE (g/cm2)",
    "USAGE UOM",
    "LND %",
    "DEFECT %",
    "COUNT",
    "LENGTH (cm)",
    "SLIT COUNT",
    "TRIM COUNT",
    "PATCH COUNT",
    "SHOE COUNT",
    "PRESS COUNT",
    "PIN COUNT",
    "CLIP COUNT",
    "CABLE COUNT",
    "RUBBER PREFORM COUNT",
    "OBSERVED MINUTES"
  ];

  if (inputColumns.includes(columnName)) return true;
  else return false;
};

export const formatInputColumn = (columnKey: string, _freezeColumn: Function) => {
  return (
    <Cell className={`${s.inputCell}`}>
      <a onClick={() => _freezeColumn(columnKey)}>{columnKey}</a>
    </Cell>
  );
};

export const isUSCostCell = (cellValue: string) => {
  const costCells = [
    "TOTAL UNIT PRICE",
    "LABOR COST",
    "OVERHEAD COST (USD)",
    "TTL LND UNIT COST",
    "NET LND $",
    "WASTE LND $",
    "GROSS LND $",
    "DEFECT LND $",
    "US LND $"
  ];

  if (costCells.includes(cellValue)) return true;
  else return false;
};

export const isUsageCell = (cellValue: string) => {
  const usageCells = ["WASTE USAGE", "GROSS USAGE", "NET USAGE"];
  if (usageCells.includes(cellValue)) return true;
  else return false;
};

export const isExchangeRateCell = (cellValue: string) => {
  if (cellValue === "Exchange Rate") return true;
  else return false;
};

export const isFtyCostCell = (cellValue: string) => {
  const costCells = ["FCTY UNIT PRICE", "UNIT PRICE UPCHG", "OUTSOURCED LABOR COST"];

  if (costCells.includes(cellValue)) return true;
  else return false;
};

export const isPercentageCell = (cellValue: string) => {
  const costCells = ["DEFECT %", "EFF %"];

  if (costCells.includes(cellValue)) return true;
  else return false;
};

export const isLndPercentageCell = (cellValue: string) => {
  if (cellValue === "LND %") return true;
  else return false;
};

export const getFormattedCell = (
  data: Array<Object>,
  rowIndex: number,
  columnKey: string,
  tableSection: ?string,
  tableTitle: ?string
) => {
  const cellValue = getProperty(data, [rowIndex, columnKey]);
  const factoryCurr = getProperty(data, [rowIndex, "FCTY CURR"]);
  const curr = getProperty(data, [rowIndex, "CURR"]);
  const isNumeric = !isNaN(parseFloat(cellValue)) && isFinite(cellValue);

  const booleanCell = cellValue === true ? "Yes" : cellValue === false ? "No" : null;
  const cellIsUsCost = isUSCostCell(columnKey);
  const cellIsFtyCost = isFtyCostCell(columnKey);
  const cellIsUsageCell = isUsageCell(columnKey);
  const cellIsExchangeRate = isExchangeRateCell(columnKey);
  const cellIsPercentage = isPercentageCell(columnKey);
  const cellIsLndPercentage = isLndPercentageCell(columnKey);

  const dataE2e =
    tableSection && tableTitle
      ? `${tableTitle}-${tableSection.replace(/ /g, "-")}-${columnKey.replace(/ /g, "-")}-${
          rowIndex
        }`
      : "";

  if (booleanCell) {
    return (
      <Cell className={`${s.cell}`}>
        <p data-e2e={dataE2e}>{booleanCell}</p>
      </Cell>
    );
  } else if (cellIsUsCost) {
    return (
      <Cell className={`${s.cell} ${s.numericCell}`}>
        <p data-e2e={dataE2e}>${numberToFixed(cellValue, 4)}</p>
      </Cell>
    );
  } else if (cellIsFtyCost) {
    return (
      <Cell className={`${s.cell} ${s.numericCell}`}>
        <p data-e2e={dataE2e}>{addCurrencySymbol(factoryCurr, numberToFixed(cellValue, 4))}</p>
      </Cell>
    );
  } else if (columnKey === "UNIT PRICE") {
    return (
      <Cell className={`${s.cell} ${s.numericCell}`}>
        <p data-e2e={dataE2e}>{addCurrencySymbol(curr, numberToFixed(cellValue, 4))}</p>
      </Cell>
    );
  } else if (cellIsUsageCell) {
    return (
      <Cell className={`${s.cell} ${s.numericCell}`}>
        <p data-e2e={dataE2e}>{numberToFixed(cellValue, 4)}</p>
      </Cell>
    );
  } else if (cellIsExchangeRate) {
    return (
      <Cell className={`${s.cell}`}>
        <p data-e2e={dataE2e}>{numberToFixed(cellValue, 7)}</p>
      </Cell>
    );
  } else if (cellIsPercentage) {
    return (
      <Cell className={`${s.cell} ${s.numericCell}`}>
        <p data-e2e={dataE2e}>{numberToFixed(cellValue, 2)}</p>
      </Cell>
    );
  } else if (cellIsLndPercentage) {
    return (
      <Cell className={`${s.cell} ${s.numericCell}`}>
        <p data-e2e={dataE2e}>{numberToFixed(cellValue, 1)}</p>
      </Cell>
    );
  } else if (isNumeric) {
    return (
      <Cell className={`${s.cell} ${s.numericCell}`}>
        <p data-e2e={dataE2e}>{cellValue}</p>
      </Cell>
    );
  } else {
    return (
      <Cell className={`${s.cell}`}>
        <p data-e2e={dataE2e}>{cellValue}</p>
      </Cell>
    );
  }
};

type MaterialSectionData = {
  "US LND $": number,
  "NET LND $": number,
  "WASTE LND $": number,
  "DEFECT LND $": number
};

const getMaterialSectionData = (lineItemData: Array<MaterialSectionData>, totalCost: Number) => {
  const mappedSummaryData = lineItemData.reduce((accumulatorObject: Object, lineItem: Object) => {
    const cost =
      lineItem["US LND $"] !== ""
        ? lineItem["US LND $"] + (accumulatorObject["Landed Cost"] || 0)
        : 0;
    const landedWasteCost =
      lineItem["WASTE LND $"] !== ""
        ? lineItem["WASTE LND $"] + (accumulatorObject["Landed Waste Cost"] || 0)
        : 0;
    const landedLostCost =
      lineItem["DEFECT LND $"] !== ""
        ? lineItem["DEFECT LND $"] + (accumulatorObject["Landed Defect Cost"] || 0)
        : 0;
    const landedNetCost =
      lineItem["NET LND $"] !== ""
        ? lineItem["NET LND $"] + (accumulatorObject["Landed Net Cost"] || 0)
        : 0;
    return {
      "Landed Net Cost": landedNetCost,
      "Net Cost %": percentOfTotal(cost, landedNetCost),
      "Landed Waste Cost": landedWasteCost,
      "Waste Cost %": percentOfTotal(cost, landedWasteCost),
      "Landed Defect Cost": landedLostCost,
      "Defect Cost %": percentOfTotal(cost, landedLostCost),
      "Landed Cost": cost,
      "% of Material Cost": ""
    };
  }, {});
  mappedSummaryData["% of Material Cost"] = percentOfTotal(
    totalCost,
    mappedSummaryData["Landed Cost"]
  );

  mappedSummaryData["Landed Waste Cost"] =
    "$" + numberToFixed(mappedSummaryData["Landed Waste Cost"], 2);
  mappedSummaryData["Landed Defect Cost"] =
    "$" + numberToFixed(mappedSummaryData["Landed Defect Cost"], 2);
  mappedSummaryData["Landed Net Cost"] =
    "$" + numberToFixed(mappedSummaryData["Landed Net Cost"], 2);
  mappedSummaryData["Landed Cost"] = "$" + numberToFixed(mappedSummaryData["Landed Cost"], 2);

  return mappedSummaryData;
};

const getCalcValue = (cost, percent) => {
  return cost * percent / 100;
};

const getLogicBomMaterialSectionData = (
  lineItemData: Array<MaterialSectionData>,
  totalCost: Number
) => {
  const mappedSummaryData = lineItemData.reduce((accumulatorObject: Object, lineItem: Object) => {
    const cost =
      lineItem["US LND $"] !== ""
        ? lineItem["US LND $"] + (accumulatorObject["Landed Cost"] || 0)
        : 0;
    const landedWasteCost =
      lineItem["WASTE LND $"] !== ""
        ? getCalcValue(lineItem["WASTE LND $"], lineItem["MTL FCST %"]) +
          (accumulatorObject["Landed Waste Cost"] || 0)
        : 0;
    const landedLostCost =
      lineItem["DEFECT LND $"] !== ""
        ? getCalcValue(lineItem["DEFECT LND $"], lineItem["MTL FCST %"]) +
          (accumulatorObject["Landed Defect Cost"] || 0)
        : 0;
    const landedNetCost =
      lineItem["NET LND $"] !== ""
        ? getCalcValue(lineItem["NET LND $"], lineItem["MTL FCST %"]) +
          (accumulatorObject["Landed Net Cost"] || 0)
        : 0;
    return {
      "Landed Net Cost": landedNetCost,
      "Net Cost %": percentOfTotal(cost, landedNetCost),
      "Landed Waste Cost": landedWasteCost,
      "Waste Cost %": percentOfTotal(cost, landedWasteCost),
      "Landed Defect Cost": landedLostCost,
      "Defect Cost %": percentOfTotal(cost, landedLostCost),
      "Landed Cost": cost,
      "% of Material Cost": ""
    };
  }, {});
  mappedSummaryData["% of Material Cost"] = percentOfTotal(
    totalCost,
    mappedSummaryData["Landed Cost"]
  );

  mappedSummaryData["Landed Waste Cost"] =
    "$" + numberToFixed(mappedSummaryData["Landed Waste Cost"], 2);
  mappedSummaryData["Landed Defect Cost"] =
    "$" + numberToFixed(mappedSummaryData["Landed Defect Cost"], 2);
  mappedSummaryData["Landed Net Cost"] =
    "$" + numberToFixed(mappedSummaryData["Landed Net Cost"], 2);
  mappedSummaryData["Landed Cost"] = "$" + numberToFixed(mappedSummaryData["Landed Cost"], 2);

  return mappedSummaryData;
};

type LaborSectionData = {
  "LABOR COST": number,
  "TOTAL STANDARD MINUTES": number,
  "TOTAL OBSERVED MINUTES": number,
  "LABOR MINUTES": number
};

const getLaborSectionData = (lineItemData: Array<LaborSectionData>, totalCost: Number) => {
  let totalInhouseCost = 0;
  let totalOutsourcedCost = 0;
  const laborSectionData = lineItemData.reduce((accumulatorObject: Object, lineItem: Object) => {
    const cost = parseFloat(lineItem["LABOR COST"]) + (accumulatorObject["Cost"] || 0);

    const standardMinutes =
      parseFloat(lineItem["TOTAL STANDARD MINUTES"]) + (accumulatorObject["Standard Minutes"] || 0);

    const observedMinutes =
      parseFloat(lineItem["TOTAL OBSERVED MINUTES"]) + (accumulatorObject["Observed Minutes"] || 0);

    const laborMinutes =
      parseFloat(lineItem["LABOR MINUTES"]) + (accumulatorObject["Total Labor Minutes"] || 0);
    if (lineItem["FCTY IN-HOUSE"] === null) {
      console.log(lineItem["FCTY IN-HOUSE"]);
    }
    if (lineItem["FCTY IN-HOUSE"] === true || lineItem["FCTY IN-HOUSE"] === "") {
      totalInhouseCost =
        parseFloat(lineItem["LABOR COST"]) + (accumulatorObject["Total In-House Cost"] || 0);
    } else {
      totalOutsourcedCost =
        parseFloat(lineItem["LABOR COST"]) + (accumulatorObject["Total Outsourced Cost"] || 0);
    }

    return {
      Cost: cost,
      "% of Labor Cost": "",
      "Standard Minutes": standardMinutes,
      "Observed Minutes": observedMinutes,
      "Total Labor Minutes": laborMinutes,
      "Total In-House Cost": totalInhouseCost,
      "Total Outsourced Cost": totalOutsourcedCost
    };
  }, {});

  laborSectionData["% of Labor Cost"] = percentOfTotal(totalCost, laborSectionData["Cost"]);
  laborSectionData["Total In-House Cost"] =
    "$" + numberToFixed(laborSectionData["Total In-House Cost"], 2);
  laborSectionData["Total Outsourced Cost"] =
    "$" + numberToFixed(laborSectionData["Total Outsourced Cost"], 2);

  laborSectionData.Cost = "$" + numberToFixed(laborSectionData.Cost, 2);

  if (
    laborSectionData["Standard Minutes"] !== undefined &&
    laborSectionData["Standard Minutes"] !== null
  ) {
    laborSectionData["Standard Minutes"] = parseFloat(
      laborSectionData["Standard Minutes"].toFixed(2)
    );
  }
  if (
    laborSectionData["Observed Minutes"] !== undefined &&
    laborSectionData["Observed Minutes"] !== null
  ) {
    laborSectionData["Observed Minutes"] = parseFloat(
      laborSectionData["Observed Minutes"].toFixed(2)
    );
  }
  if (
    laborSectionData["Total Labor Minutes"] !== undefined &&
    laborSectionData["Total Labor Minutes"] !== null
  ) {
    laborSectionData["Total Labor Minutes"] = parseFloat(
      laborSectionData["Total Labor Minutes"].toFixed(2)
    );
  }
  return laborSectionData;
};

export const getSectionData = (
  lineItemData: Array<Object>,
  tableTitle: string,
  totalCost: Number,
  isNby: boolean
): Object => {
  if (tableTitle === "Material") {
    return isNby
      ? getLogicBomMaterialSectionData(lineItemData, totalCost)
      : getMaterialSectionData(lineItemData, totalCost);
  } else if (tableTitle === "Labor") {
    return getLaborSectionData(lineItemData, totalCost);
  } else {
    return {};
  }
};



// WEBPACK FOOTER //
// ./src/components/CBDTables/LineItemTables/LineItemTablesContainer/LineItemTableSubSection/LineItemTableCellHelpers.js